import { useEffect } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { RichEditorPreset } from '../types';

interface PresetReplaceConfirmModalProps {
    preset: RichEditorPreset;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * Asks the user to confirm overwriting non-empty editor content with a
 * preset. Fires from both the empty-state cards and the toolbar dropdown
 * when a preset is picked while the doc already has text/images.
 * Self-contained (no toast/provider deps) since RichEditor is shared.
 */
export function PresetReplaceConfirmModal({
    preset,
    onCancel,
    onConfirm
}: PresetReplaceConfirmModalProps) {
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCancel();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onCancel]);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-35 z-50 flex items-center justify-center"
            onClick={onCancel}
        >
            <div
                className="bg-white rounded-xl w-[440px] shadow-xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="preset-replace-title"
            >
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />
                        <span
                            id="preset-replace-title"
                            className="font-bold text-[15px] text-gray-800"
                        >
                            Replace current content?
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-3">
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-amber-50 border border-amber-300">
                        <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-amber-900 leading-relaxed">
                            Loading this preset will replace what you have already typed. Your edits
                            will be lost.
                        </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3">
                        <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                            Preset to load
                        </div>
                        <div className="text-sm font-semibold text-gray-800">{preset.label}</div>
                        {preset.description && (
                            <div className="text-xs text-gray-600 leading-snug mt-1">
                                {preset.description}
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-xs font-bold bg-white hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        className="px-4 py-2 rounded-md bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 transition-colors"
                    >
                        Replace
                    </button>
                </div>
            </div>
        </div>
    );
}
